import type { Account, BillingDetails } from '@/api/types';

/** What the billing form holds while the parent types; the state is asked in India only. */
export interface BillingForm {
  name: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  state: string;
}

export type BillingField = 'name' | 'phone' | 'addressLine1' | 'city' | 'postalCode' | 'state';
export type BillingErrors = Partial<Record<BillingField, string>>;

/** Same shape the API accepts, so a number that passes here is not refused there. */
const PHONE = /^\+?[0-9 ()-]{7,20}$/;

/** Starts from what is on record, then falls back to the account's own name and number. */
export const billingFormOf = (account: Account | null | undefined): BillingForm => ({
  name: account?.billing?.name ?? account?.name ?? '',
  phone: account?.billing?.phone ?? account?.phone ?? '',
  addressLine1: account?.billing?.addressLine1 ?? '',
  addressLine2: account?.billing?.addressLine2 ?? '',
  city: account?.billing?.city ?? '',
  postalCode: account?.billing?.postalCode ?? '',
  state: account?.billingState ?? '',
});

/** Error keys (through `t()`), or the details ready to send. India also needs a 6-digit PIN and a state. */
export const validateBilling = (
  form: BillingForm,
  india: boolean,
): { ok: true; billing: BillingDetails; state?: string } | { ok: false; errors: BillingErrors } => {
  const errors: BillingErrors = {};
  const postal = form.postalCode.trim();
  if (!form.name.trim()) errors.name = 'checkout.billing.err.name';
  if (!PHONE.test(form.phone.trim())) errors.phone = 'checkout.billing.err.phone';
  if (!form.addressLine1.trim()) errors.addressLine1 = 'checkout.billing.err.address';
  if (!form.city.trim()) errors.city = 'checkout.billing.err.city';
  if (india ? !/^\d{6}$/.test(postal) : postal.length < 3 || postal.length > 12) errors.postalCode = 'checkout.billing.err.postal';
  if (india && !form.state) errors.state = 'checkout.stateRequired';
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    billing: {
      name: form.name.trim(),
      phone: form.phone.trim(),
      addressLine1: form.addressLine1.trim(),
      ...(form.addressLine2.trim() ? { addressLine2: form.addressLine2.trim() } : {}),
      city: form.city.trim(),
      postalCode: postal,
    },
    ...(india ? { state: form.state } : {}),
  };
};

/** True once the account already holds everything the invoice needs. */
export const billingComplete = (account: Account | null | undefined, india: boolean): boolean =>
  !!account?.billing && validateBilling(billingFormOf(account), india).ok;
